import { Component, h } from 'preact'
import Icon from '../assets'
import Phrase from '../../libs/translationsUtils/phrase'
import UserAvatar from '../User/UserAvatar'
import LazyLoader from '../lazy-loading/LazyLoader'
import tracker from '../../components/tracker/publish'

interface Member {
  id: number
  url: string
  avatar: string
}

export interface Group {
  id: string
  title: string
  url: string
  totalMembers: number
  totalTopics: number
  type: number
  recentMembers: Member[]
  thumbnail: string
  tags: string[]
  isUserMember?: boolean
}

interface GroupItemProps extends Group {
  isUserMember?: boolean
  isEvenCount: boolean
}

const PUBLIC_GROUP_TYPE = 4
const PRIVATE_GROUP_TYPE = 5
const HIDDEN_GROUP_TYPE = 6

export class GroupItem extends Component<GroupItemProps> {
  render (group: GroupItemProps) {
    return (
      <a class={`group-overview-item card-widget-wrapper ${group.isEvenCount ? 'group-overview-item--even' : ''}`} href={group.url}>
        <LazyLoader type='background' src={group.thumbnail} classNames='group-overview-item__top'>
          {
            group.type === PUBLIC_GROUP_TYPE
              ? (
                <div className='group-overview-item__top-content'>
                  {
                    group.recentMembers.concat([]).reverse().map(member => {
                      if (member.id.toString() === window.inSidedData.user.userid) {
                        return null
                      }

                      return (
                        <div key={member.id} className='group-overview-item__member'>
                          <span className='group-overview-item__member-link'>
                            <UserAvatar
                              user={member}
                              extraClass='avatar avatar--S'
                              showRank
                            />
                          </span>
                        </div>
                      )
                    })
                  }

                  {
                    group.isUserMember
                      ? <div key='me' className='group-overview-item__member'>
                        <span className='group-overview-item__member-link'>
                          <Icon icon='checkmark' height={30} width={30} />
                        </span>
                      </div>
                      : null
                  }
                </div>
                )
              : null
          }
        </LazyLoader>
        <div class='group-overview-item__bottom'>
          <h4 class='card-widget-title'>{group.title}</h4>
          <div class='group-overview-item__meta group-overview-item__meta--members-total text--meta'>
            {group.type === PUBLIC_GROUP_TYPE
              ? (
                <div>
                  <Icon width={20} height={20} icon='people' /> <span class='group-overview-item__meta-text'>{group.totalMembers} <Phrase p='Members' /></span>
                </div>
                )
              : null}

            {group.type === PRIVATE_GROUP_TYPE
              ? (
                <div style='display: flex'>
                  <Icon width={20} height={20} icon='lock' /><span className='group-overview-item__meta-text'><Phrase p='groups.private.group' /></span>
                </div>
                )
              : null}

            {group.type === HIDDEN_GROUP_TYPE
              ? (
                <div style='display: flex'>
                  <Icon width={20} height={20} icon='eye-cross' /><span className='group-overview-item__meta-text'><Phrase p='groups.hidden.group' /></span>
                </div>
                )
              : null}

          </div>
          <div class='group-overview-item__meta group-overview-item__meta--topics-total text--meta'>
            <Icon width={20} height={20} icon='conversation' /> <span class='group-overview-item__meta-text'>
              {
              group.totalTopics === 0
                ? (
                  <Phrase p='groups.no.topics.yet' />
                  )
                : (
                  <span>{group.totalTopics} <Phrase p='Topic|Topics' count={{ n: group.totalTopics.toString() }} /></span>
                  )
            }
            </span>
          </div>
        </div>
      </a>
    )
  }
}

interface Props {
  groups: {
    myGroups: Group[]
    otherGroups: Group[]
  }
}

interface State {
  selectedTags: string[]
  tagCount: Record<string, number>
}

class GroupsOverview extends Component<Props, State> {
  constructor (props: Props) {
    super()
    const tagCount: State['tagCount'] = {}
    props.groups.otherGroups.forEach(group => {
      group.tags.forEach(tag => {
        if (tagCount[tag]) {
          tagCount[tag]++
        } else {
          tagCount[tag] = 1
        }
      })
    })

    this.state = {
      selectedTags: [],
      tagCount
    }

    this.selectTag = this.selectTag.bind(this)
  }

  selectTag (item: string) {
    if (this.state.selectedTags.includes(item)) {
      this.setState({
        selectedTags: this.state.selectedTags.filter(tag => tag !== item)
      })
    } else {
      this.setState({
        selectedTags: [...this.state.selectedTags, item]
      })
    }

    tracker.trackEvent('userevent', {
      event_source: 'control', // mixpanel-forwarder is ignoring any other type of event
      type: 'Group overview tag clicked',
      name: item
    })
  }

  render ({ groups }: Props, { selectedTags, tagCount }: State) {
    const { myGroups, otherGroups } = groups

    const allGroupTags = otherGroups.reduce<string[]>((acc, group) => {
      group.tags.forEach(tag => {
        if (!acc.includes(tag)) {
          acc.push(tag)
        }
      })
      return acc
    }, [])

    const filteredGroups = otherGroups.filter(group => {
      if (!selectedTags.length) return true
      return selectedTags.every(tag => group.tags.includes(tag))
    })

    const sortedTags = allGroupTags.sort((a, b) => {
      const aIsSelected = selectedTags.includes(a)
      const bIsSelected = selectedTags.includes(b)
      if (aIsSelected && !bIsSelected) {
        return -1
      } else if (!aIsSelected && bIsSelected) {
        return 1
      }

      if (tagCount[a] !== tagCount[b]) {
        return tagCount[a] > tagCount[b] ? -1 : 1
      }

      return a > b ? 1 : -1
    }).filter(tag => filteredGroups.some(g => g.tags.includes(tag)))

    return (
      <div class='group-overview-wrapper'>
        <div class='sitewidth text-center'>
          <h1 class='group-overview-wrapper-title'><Phrase p='groups' /></h1>
        </div>

        {myGroups.length
          ? <div class='groups--mine'>
            <h4 class='group-overview-title'><Phrase p='groups.my.groups' /></h4>

            <div class='group-overview'>
              {
            myGroups.map(group => <GroupItem key={group.id} isEvenCount={myGroups.length === 2 || myGroups.length === 4} isUserMember {...group} />)
          }
            </div>
          </div>
          : null}
        {
        otherGroups.length
          ? (
            <div className='groups--other'>
              {myGroups.length ? <h4 className='group-overview-title'><Phrase p='groups.find.new.group' /></h4> : null}

              {allGroupTags.length
                ? <ul className='public-tags list list--float'>
                  {sortedTags.map(item => (
                    <li key={item}>
                      <button
                        type='button'
                        style='font-size: 14px'
                        className={`tag--pill qa-tag-pill ${selectedTags.includes(item) ? 'tag--active' : ''}`}
                        onClick={_ => this.selectTag(item)}
                      >
                        {item}
                      </button>
                    </li>
                  ))}
                </ul>
                : null}

              <div className='group-overview'>
                {
                  filteredGroups.map(group => (
                    <GroupItem
                      key={group.id}
                      isEvenCount={filteredGroups.length === 2 || filteredGroups.length === 4} {...group}
                    />
                  ))
                }
              </div>
            </div>
            )
          : null
      }
      </div>
    )
  }
}

export default GroupsOverview
